"use client";

import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ChannelFormulaModal } from "../channel-formula-modal";
import { ChannelPrecisionModal } from "../channel-precision-modal";
import { ChannelThresholdModal } from "../channel-threshold-modal";
import { ChannelCard } from "../channel-config/channel-card";
import { useDataloggerWizardStore } from "../../hooks/use-datalogger-form";
import { type ChannelFormula, type FloatPrecision } from "../../schema/datalogger.schema";

function toggleSetMember(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

export function StepChannels({
  invalidFieldKey = null,
  validationErrors = [],
  onFieldEdit,
  lockedChannelIds,
}: {
  invalidFieldKey?: string | null;
  validationErrors?: string[];
  onFieldEdit?: () => void;
  lockedChannelIds?: ReadonlySet<string>;
}) {
  const channels = useDataloggerWizardStore((s) => s.channels);
  const updateChannel = useDataloggerWizardStore((s) => s.updateChannel);
  const addChannel = useDataloggerWizardStore((s) => s.addChannel);
  const removeChannel = useDataloggerWizardStore((s) => s.removeChannel);
  const duplicateChannel = useDataloggerWizardStore((s) => s.duplicateChannel);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [advancedIds, setAdvancedIds] = useState<Set<string>>(new Set());

  const [formulaForId, setFormulaForId] = useState<string | null>(null);
  const [thresholdForId, setThresholdForId] = useState<string | null>(null);
  const [precisionForId, setPrecisionForId] = useState<string | null>(null);

  const formulaRow = formulaForId ? channels.find((c) => c.id === formulaForId) : undefined;
  const thresholdRow = thresholdForId ? channels.find((c) => c.id === thresholdForId) : undefined;
  const precisionRow = precisionForId ? channels.find((c) => c.id === precisionForId) : undefined;

  const prevLenRef = useRef(channels.length);

  useEffect(() => {
    const alive = new Set(channels.map((c) => c.id));
    const grew = channels.length > prevLenRef.current;
    prevLenRef.current = channels.length;

    setExpandedIds((prev) => {
      const next = new Set([...prev].filter((id) => alive.has(id)));
      if (grew && channels.length > 0) {
        const last = channels[channels.length - 1];
        if (last) {
          next.add(last.id);
        }
      } else if (next.size === 0 && channels[0]) {
        next.add(channels[0].id);
      }
      return next;
    });
    setAdvancedIds((prev) => new Set([...prev].filter((id) => alive.has(id))));
  }, [channels]);

  useEffect(() => {
    const match = invalidFieldKey?.match(/^channel\.(\d+)\./);
    if (!match) {
      return;
    }
    const index = Number(match[1]);
    const row = channels[index];
    if (!row) {
      return;
    }
    setExpandedIds((prev) => new Set(prev).add(row.id));
  }, [channels, invalidFieldKey]);

  return (
    <div className="space-y-ds-5">
      <div className="flex flex-wrap items-end justify-between gap-ds-3 rounded-[20px] border border-card-border bg-muted-bg/20 px-ds-5 py-ds-4">
        <div>
          <p className="text-title font-semibold text-foreground">Signal channels</p>
          <p className="mt-ds-1 text-caption text-muted">
            Define what this logger publishes. Expand a row to edit type, units, thresholds, and chart defaults.
          </p>
        </div>
        <p className="text-caption font-medium text-muted">
          {channels.length} channel{channels.length === 1 ? "" : "s"}
        </p>
      </div>

      <ul className="space-y-ds-3" aria-label="Channels">
        {channels.map((row, index) => {
          const peerKeys = channels.filter((c) => c.id !== row.id).map((c) => c.channelKey);
          return (
            <li key={row.id}>
              <ChannelCard
                row={row}
                index={index}
                peerKeys={peerKeys}
                invalidFieldKey={invalidFieldKey}
                validationErrors={validationErrors}
                keyLocked={lockedChannelIds?.has(row.id) ?? false}
                expanded={expandedIds.has(row.id)}
                onToggleExpand={() => setExpandedIds((s) => toggleSetMember(s, row.id))}
                advancedOpen={advancedIds.has(row.id)}
                onToggleAdvanced={() => setAdvancedIds((s) => toggleSetMember(s, row.id))}
                onUpdate={(updater) => {
                  onFieldEdit?.();
                  updateChannel(row.id, updater);
                }}
                onRemove={() => {
                  onFieldEdit?.();
                  removeChannel(row.id);
                }}
                onDuplicate={() => {
                  onFieldEdit?.();
                  duplicateChannel(row.id);
                }}
                canRemove={channels.length > 1}
                onOpenThreshold={() => setThresholdForId(row.id)}
                onOpenFormula={() => setFormulaForId(row.id)}
                onOpenPrecision={() => setPrecisionForId(row.id)}
              />
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end border-t border-card-border pt-ds-4">
        <button
          type="button"
          className="btn btn-secondary gap-ds-2"
          onClick={() => {
            onFieldEdit?.();
            addChannel();
          }}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add channel
        </button>
      </div>

      <ChannelFormulaModal
        open={Boolean(formulaForId && formulaRow)}
        onClose={() => setFormulaForId(null)}
        currentChannelId={formulaForId ?? ""}
        channels={channels}
        initial={formulaRow?.formula ?? null}
        onSave={(f: ChannelFormula) => {
          if (!formulaForId) {
            return;
          }
          updateChannel(formulaForId, (c) => ({ ...c, formula: f }));
        }}
      />

      <ChannelThresholdModal
        open={Boolean(thresholdForId && thresholdRow)}
        onClose={() => setThresholdForId(null)}
        channel={thresholdRow ?? null}
        onApply={(patch) => {
          if (!thresholdForId) {
            return;
          }
          updateChannel(thresholdForId, (c) => ({ ...c, ...patch }));
        }}
      />

      <ChannelPrecisionModal
        open={Boolean(precisionForId && precisionRow)}
        onClose={() => setPrecisionForId(null)}
        channel={precisionRow ?? null}
        onApply={(p: FloatPrecision) => {
          if (!precisionForId) {
            return;
          }
          updateChannel(precisionForId, (c) => ({ ...c, floatPrecision: p }));
        }}
      />
    </div>
  );
}
